/**
 * Legale **Stöße** (PUSH) — die Aktion des Stoßers.
 *
 * Der Stoßer führt kein normales CAPTURE aus (§18). Seit der Neufassung am 2026-10-02
 * wirkt er **aus der Entfernung** (`push.range`) und **bleibt dabei stehen**; ob und wie
 * weit er nachrückt, ist ein Balancing-Parameter (`push.pusherAdvance`, Default 0).
 *
 * Vier Dinge unterscheiden den PUSH von jeder anderen Aktion:
 *
 * 1. **Er kennt den Ring nicht.** Ziel und Verschiebung folgen immer einer **geraden**
 *    Hex-Richtung. Weil die sechs Ringkanten gerade Linien sind, bleibt eine auf einer
 *    Kante gestoßene Figur zufällig auf dem Ring — an einer Ringecke verlässt sie ihn.
 *    Das ist Geometrie, keine Sonderregel (`docs/GAME_RULES.md`, Abschnitt 2).
 * 2. **Teil-Push:** die gestoßene Figur rutscht so weit, wie Platz ist — `push.distance`
 *    ist eine Obergrenze, keine Bedingung. Ist aber **direkt** hinter ihr kein nutzbares
 *    Feld, ist der PUSH nicht möglich (§18).
 * 3. **Der Rand ist das Ende des Weges, kein Hindernis.** Bleibt Restdistanz übrig, verlässt
 *    die Figur das Brett und gilt als **geschlagen** (`push.canPushOffBoard`). Der Stoßer
 *    selbst verlässt das Brett nie.
 * 4. **Keine Kettenverschiebung** — eine getroffene Figur schiebt keine weitere an.
 *
 * Ein Fähnrich auf dem zentralen Zielfeld ist nicht stoßbar (§18). Außerhalb ist er eine
 * normale Figur und kann auch über den Rand gestoßen werden.
 *
 * **Gestoßen wird jede Figur, auch die eigene** (Zyklus R2, 2026-10-07). Das ist eine
 * Entscheidung des Users aus dem Spieltesten, und sie hat zwei Seiten: der Stoßer trägt den
 * eigenen oder verbündeten Fähnrich schneller ins Zentrum, als dessen Bewegung reicht — und er
 * kann die eigene Figur ebenso über den Rand werfen, wo sie als geschlagen gilt und im
 * **eigenen** Respawn-Pool landet (§22). Beides ist gewollt; das Zweite ausdrücklich als
 * möglicher Teil der Strategie.
 */
import { isOnBoard } from './board.js';
import { isOccupied, pieceAt, requirePiece } from './board-state.js';
import { goalCellBlocking, isGoalCell, mayEnterGoalCell } from './goal-cell.js';
import { HEX_DIRECTIONS, hexAdd } from './hex.js';
import { pushConfig } from './pieces.js';
/** Darf die gestoßene Figur auf diesem Feld zu stehen kommen? */
function canVictimRestOn(context, cell, victim) {
    if (isOccupied(context.state, cell))
        return false;
    // „Nicht betretbar" gilt auch, wenn eine Figur dorthin geschoben würde
    // (bestätigt 2026-10-02, docs/OPEN_QUESTIONS.md Nr. 19).
    if (isGoalCell(cell) && !mayEnterGoalCell(victim.type))
        return false;
    return true;
}
/**
 * Schiebt die Figur bis zu `distance` Felder in Richtung `direction` und meldet, wo sie
 * zu stehen kommt — oder dass sie das Brett verlassen hat.
 *
 * `undefined` bedeutet: der PUSH ist nicht möglich, weil sich die Figur nicht einen
 * einzigen Schritt bewegen kann.
 */
function slideVictim(context, victim, direction, distance, canPushOffBoard) {
    let current = victim.at;
    let moved = 0;
    for (let step = 1; step <= distance; step++) {
        const next = hexAdd(current, direction);
        if (!isOnBoard(context.board, next)) {
            // Der Rand ist das Ende des Weges. Mit canPushOffBoard trägt die Restdistanz die
            // Figur hinaus; ohne sie bleibt sie auf dem letzten Feld stehen.
            if (!canPushOffBoard)
                break;
            return { to: undefined, captured: true };
        }
        if (!canVictimRestOn(context, next, victim))
            break;
        current = next;
        moved += 1;
    }
    // Kein einziger Schritt möglich: §18 — ist direkt hinter der Figur kein nutzbares Feld,
    // ist der PUSH nicht möglich.
    if (moved === 0)
        return undefined;
    return { to: current, captured: false };
}
/**
 * Wie weit der Stoßer nachrückt.
 *
 * Gekappt an der **tatsächlichen** Endposition der gestoßenen Figur, nicht am
 * konfigurierten `push.distance` — und nie über den Brettrand hinaus
 * (bestätigt 2026-10-02, `docs/OPEN_QUESTIONS.md` Nr. 15).
 */
function advancePusher(context, pusher, victim, direction, advance, blockedFrom) {
    let current = pusher.at;
    for (let step = 1; step <= advance; step++) {
        const next = hexAdd(current, direction);
        if (!isOnBoard(context.board, next))
            break;
        // Nie auf oder hinter die gestoßene Figur.
        if (blockedFrom !== undefined && next.q === blockedFrom.q && next.r === blockedFrom.r)
            break;
        // Wichtig: gerechnet wird auf dem Zustand VOR der Verschiebung. Die gestoßene Figur
        // steht dort noch, hat das Feld aber bereits verlassen — sie darf den Stoßer nicht
        // blockieren. Jede ANDERE Figur schon.
        const occupant = pieceAt(context.state, next);
        if (occupant !== undefined && occupant.id !== victim.id)
            break;
        if (isGoalCell(next) && !mayEnterGoalCell(pusher.type))
            break;
        current = next;
    }
    return current;
}
/** Alle Stöße, die diese Figur in diesem Zug ausführen darf. */
export function getLegalPushes(context, pieceId) {
    const pusher = requirePiece(context.state, pieceId);
    const push = pushConfig(context.config, pusher.type);
    if (push === undefined)
        return [];
    const pushes = [];
    const maxRange = push.range.max;
    for (const direction of HEX_DIRECTIONS) {
        let current = pusher.at;
        for (let step = 1; step <= maxRange; step++) {
            current = hexAdd(current, direction);
            if (!isOnBoard(context.board, current))
                break;
            const occupant = pieceAt(context.state, current);
            const blocking = goalCellBlocking(context.config, current, pusher.type);
            if (occupant !== undefined) {
                const reachable = step >= push.range.min;
                /*
                  **Wer gestoßen werden darf: jeder** (Zyklus R2, 2026-10-07). Bis dahin stand hier
                  `occupant.owner !== pusher.owner` — eine Bedingung, die den **Besitzer** verglich und
                  nicht die Seite: verbündete Figuren waren also längst stoßbar, nur die eigenen nicht.
                  Seit der Entscheidung des Users gibt es die Ausnahme gar nicht mehr.
        
                  Die Ausnahme bleibt: ein Fähnrich auf dem Zielfeld ist nicht stoßbar (§18) — **auch
                  der eigene**. Wer ihn hineinstößt, bekommt ihn nicht wieder heraus.
        
                  **Seit `R11` ist das eine Option** (`push.canPushFlagBearerFromGoal`, Nr. 51, Vorgabe
                  aus). Ist sie an, entfällt der Schutz für jeden Fähnrich. Der Rest der Stoßregel gilt
                  unverändert, und `slideVictim` behandelt den Stoß wie jeden anderen: Hinausstoßen ist
                  kein Schlagen.
                */
                const protectedOnGoal = isGoalCell(current) && !push.canPushFlagBearerFromGoal;
                if (reachable && !protectedOnGoal) {
                    const slide = slideVictim(context, occupant, direction, push.distance, push.canPushOffBoard);
                    if (slide !== undefined) {
                        pushes.push({
                            victimAt: current,
                            victim: occupant.id,
                            victimTo: slide.to,
                            capturedByPush: slide.captured,
                            pusherTo: advancePusher(context, pusher, occupant, direction, push.pusherAdvance, slide.to),
                        });
                    }
                }
                // Die erste Figur auf der Linie beendet sie — der Stoßer stößt nicht hindurch.
                break;
            }
            // Ein leeres Feld: weiter, solange die Zielfeld-Barriere es zulässt.
            if (blocking.hard || blocking.soft)
                break;
        }
    }
    return pushes;
}
//# sourceMappingURL=legal-pushes.js.map